import React, { Fragment, ReactElement } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Backdrop,
  CircularProgress,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router'; // import the react-router-dom components
import TranslatedText from '../../components/atoms/translated_text';
import CentralBankPaymentActions from '../../redux/reducers/payment_central_bank_reducer/actions';
import useStyles from './styles';
import DatatableList from './components/datatable';
import clsx from 'clsx';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import { TableDefaultFetchValues, Translations } from './configuration';
import * as AuthSelectors from '../../redux/reducers/gl_auth_reducer/selectors';
import { Roles } from 'config';
import useQuery from 'custom_hooks/useQuery';
import BaseModal from '../../components/molecules/base_modal';
import { ModalIDs } from '../../redux/reducers/gl_modal_reducer/types';
import AddVatReturnCorrectionForm from './components/match_payment';

const CentralBankPaymentsListPage = (props: any) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const ROLE = AuthSelectors.useSelectUserRole();

  const query = useQuery();

  const sortField: string = query.get('sortField')
    ? (query.get('sortField') as string)
    : TableDefaultFetchValues.TABLE_DEFAULT_SORT_KEY;
  const page: number = query.get('page')
    ? Number(query.get('page'))
    : TableDefaultFetchValues.TABLE_DEFAULT_PAGE;
  const pageSize: number = query.get('pageSize')
    ? Number(query.get('pageSize'))
    : TableDefaultFetchValues.TABLE_DEFAULT_PAGE_SIZE;
  const order: 'asc' | 'desc' = query.get('order')
    ? (query.get('order') as 'asc' | 'desc')
    : TableDefaultFetchValues.TABLE_DEFAULT_ORDER;
  const account = query.get('account');
  const statusCode = query.get('statusCode');
  const dateFrom = query.get('dateFrom');
  const dateTo = query.get('dateTo');

  React.useEffect(() => {
    dispatch(
      CentralBankPaymentActions.setCentralBankPaymentsAction({
        sortField: sortField,
        page: page,
        pageSize: pageSize,
        order: order,
        account: account,
        statusCode: statusCode,
        dateFrom: dateFrom,
        dateTo: dateTo,
      })
    );
    //}, [dispatch, searchParams]);
  }, [dispatch, account, statusCode, dateFrom, dateTo]);

  React.useEffect(() => {
    dispatch(CentralBankPaymentActions.SetCentralBankPaymentAccountsAction());
    dispatch(
      CentralBankPaymentActions.SetCentralBankPaymentStatusCodesAction()
    );
  }, [dispatch]);
  const [open, setOpen] = React.useState(false);

  const fetchPcbPayments = () => {
    setOpen(true);
    dispatch(
      CentralBankPaymentActions.fetchCentralBankPayments({
        onSuccess: () => {
          dispatch(
            CentralBankPaymentActions.setCentralBankPaymentsAction({
              sortField: sortField,
              page: page,
              pageSize: pageSize,
              order: order,
              account: account,
              statusCode: statusCode,
              dateFrom: dateFrom,
              dateTo: dateTo,
            })
          );
          setOpen(false);
        },
      })
    );
  };
  const matchCentralBankPayments = () => {
    setOpen(true);
    dispatch(
      CentralBankPaymentActions.matchCentralBankPayments({
        onSuccess: () => {
          dispatch(
            CentralBankPaymentActions.setCentralBankPaymentsAction({
              sortField: sortField,
              page: page,
              pageSize: pageSize,
              order: order,
              account: account,
              statusCode: statusCode,
              dateFrom: dateFrom,
              dateTo: dateTo,
            })
          );
          setOpen(false);
        },
      })
    );
  };

  return (
    /*================== Payment List Page ==================*/
    <div>
      {/*================== Payment List Page - Header ==================*/}
      <Paper
        className={clsx(classes.titleLayout)}
        elevation={0}
        variant={'outlined'}
        square
      >
        <div className={classes.header}>
          {/* Payment List Page - Header - Title, Icon & Actions */}
          <Box display="flex" alignItems={'center'}>
            {/* Icon */}
            <AssignmentOutlinedIcon style={{ marginRight: '.75rem' }} />
            {/* Title */}
            <Typography component={'p'} variant={'h6'}>
              <TranslatedText
                textMap={Translations.page_title}
                defaultText={Translations.page_title.English}
              />
            </Typography>
            <Backdrop className={classes.backdrop} open={open}>
              <CircularProgress color="inherit" />
            </Backdrop>
          </Box>
          {ROLE === Roles.Admin && (
            <Box display="flex" alignItems={'center'}>
              <Button
                className={classes.header_actions}
                onClick={matchCentralBankPayments}
              >
                <TranslatedText textMap={Translations.match_payments} />
              </Button>
              <Button
                className={classes.header_actions}
                onClick={fetchPcbPayments}
              >
                <TranslatedText textMap={Translations.fetch_payments} />
              </Button>
            </Box>
          )}
        </div>
      </Paper>
      {/*--------------- end of Payment List Page - Header ---------------*/}
      {/*================== Payment List Page - Body ==================*/}
      <div style={{ padding: 12 }}>
        {/*================== Payment List Page - Body - Payment List Section ==================*/}
        <div className={classes.cardLayout}>
          {/* Payment List Page - Body - Payment List Section - Title */}
          <Typography
            component={'p'}
            variant={'h5'}
            style={{ marginBottom: '.5rem' }}
          >
            <TranslatedText
              textMap={Translations.table_title}
              defaultText={Translations.table_title.English}
            />
          </Typography>

          {/* Payment List Page - Body - Payment List Section - Description */}
          <Typography component={'p'} variant={'subtitle1'}>
            <TranslatedText
              textMap={Translations.description}
              defaultText={Translations.description.English}
            />
          </Typography>

          {/* Payment List Page - Body - Payment List Section - Payment List Table */}
          <div className={classes.tableList}>
            <DatatableList />
          </div>
        </div>
        <FormModals />
        {/*--------------- end of Payment List Page - Body - Payment List Section ---------------*/}
      </div>
    </div>
  );
};

const FormModals = (): ReactElement => {
  return (
    <Fragment>
      <BaseModal
        modalID={ModalIDs.MODAL_MATCH_CENTRAL_BANK_PAYMENT}
        renderHeader={() => (
          <TranslatedText textMap={Translations.match_payments} />
        )}
        // (props) => <AddPaymentForm {...props} />
        renderContent={(props) => <AddVatReturnCorrectionForm {...props} />}
      />
      <BaseModal
        modalID={ModalIDs.MODAL_CENTRAL_BANK_PAYMENT_ERROR}
        renderHeader={() => <TranslatedText textMap={Translations.error} />}
        // (props) => <AddPaymentForm {...props} />
        renderContent={(props) => (
          <form noValidate>
            <Box width={500}>{props.error}</Box>
          </form>
        )}
      />
    </Fragment>
  );
};

export default withRouter(CentralBankPaymentsListPage);
